// services/baseballService.js
import internationalLeaguesData from '../../../pages/sport-dashboards/baseball/configs/international-baseball-leagues.json';

class BaseballService {
  constructor() {
    this.baseURL = 'https://v1.baseball.api-sports.io';
    this.apiKey = process.env.REACT_APP_FOOTBALL_API_KEY; // Same API key
    this.debug = false; // Disable debugging in production
    this.cache = {};
    this.internationalLeagues = internationalLeaguesData.baseball_leagues || [];
  }

  // Method to manually set API key if env vars aren't working
  setApiKey(key) {
    this.apiKey = key;
    this.log('API key manually set:', { keyLength: key ? key.length : 0 });
  }

  log(message, data = null) {
    if (this.debug) {
      console.log(`[Baseball API] ${message}`, data || ''); 
    }
  }

  // Cache management
  getCacheKey(endpoint, params) {
    return `baseball_${endpoint}_${JSON.stringify(params)}`;
  }

  setCache(key, data, ttl = 300000) { // 5 minutes default TTL
    this.cache[key] = {
      data,
      timestamp: Date.now(),
      ttl
    };
  }

  getCache(key) {
    const cached = this.cache[key];
    if (!cached) return null;
    
    if (Date.now() - cached.timestamp > cached.ttl) {
      delete this.cache[key];
      return null;
    }
    
    return cached.data;
  }

  clearCache() {
    this.cache = {};
    this.log('Cache cleared');
  }

  async makeRequest(endpoint, params = {}) {
    // Check cache first
    const cacheKey = this.getCacheKey(endpoint, params);
    const cachedData = this.getCache(cacheKey);
    if (cachedData) {
      this.log(`Cache hit for ${endpoint}`, params);
      return cachedData;
    }

    this.log(`Making request to ${endpoint}`, params);
    
    if (!this.apiKey) {
      this.log('API Key Debug Info:', {
        envVar: process.env.REACT_APP_FOOTBALL_API_KEY,
        envVarType: typeof process.env.REACT_APP_FOOTBALL_API_KEY,
        envVarLength: process.env.REACT_APP_FOOTBALL_API_KEY ? process.env.REACT_APP_FOOTBALL_API_KEY.length : 0,
        allReactAppVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'))
      });
      throw new Error(`Baseball API key not configured. 
Environment variable REACT_APP_FOOTBALL_API_KEY is: ${process.env.REACT_APP_FOOTBALL_API_KEY}
Available REACT_APP_ variables: ${Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')).join(', ')}

Please:
1. Ensure your .env file is in the root directory (same level as package.json)
2. Restart your development server after adding the variable
3. Make sure the .env file contains: REACT_APP_FOOTBALL_API_KEY=your_key_here
4. Or use BaseballService.setApiKey('your_key_here') as a temporary workaround`);
    }

    const url = new URL(`${this.baseURL}${endpoint}`);
    
    // Add query parameters
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        url.searchParams.append(key, params[key]);
      }
    });

    this.log(`Request URL: ${url.toString()}`);

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'x-rapidapi-host': 'v1.baseball.api-sports.io',
          'x-rapidapi-key': this.apiKey,
          'Content-Type': 'application/json'
        }
      });

      this.log(`Response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        this.log(`Error response:`, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      this.log(`Response data:`, data);

      // Check for API errors in response
      if (data.errors && Object.keys(data.errors).length > 0) {
        this.log(`API errors:`, data.errors);
        // Handle different error formats
        const errorMessage = typeof data.errors === 'string' 
          ? data.errors 
          : Object.values(data.errors).flat().join(', ');
        throw new Error(`API Error: ${errorMessage}`);
      }
      
      // Check if response indicates no results
      if (data.results === 0) {
        this.log(`No results found for ${endpoint}`, params);
      }

      // Cache successful responses with different TTLs based on data type
      let ttl = 300000; // 5 minutes default
      if (endpoint.includes('/games')) ttl = 15000; // 15 seconds for live games
      if (endpoint.includes('/standings')) ttl = 3600000; // 1 hour for standings
      if (endpoint.includes('/countries') || endpoint.includes('/leagues')) ttl = 86400000; // 24 hours for static data

      this.setCache(cacheKey, data, ttl);
      return data;
    } catch (error) {
      this.log(`Request failed:`, error.message);
      // Silently propagate error - let calling code decide how to handle
      throw error;
    }
  }

  // CORE ENDPOINTS

  // Get timezone list
  async getTimezones() {
    const data = await this.makeRequest('/timezone');
    return data.response || [];
  }

  // Get seasons (requires league)
  async getSeasons(league) {
    if (!league) {
      // Return empty array if no league provided
      return { response: [] };
    }
    return await this.makeRequest('/seasons', { league });
  }

  // Get countries
  async getCountries(id = null, name = null, code = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (name) params.name = name;
    if (code) params.code = code;
    if (search) params.search = search;
    return await this.makeRequest('/countries', params);
  }

  // Get leagues/competitions
  async getLeagues(id = null, name = null, countryId = null, country = null, type = null, season = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (name) params.name = name;
    if (countryId) params.country_id = countryId;
    if (country) params.country = country;
    if (type) params.type = type;
    if (season) params.season = season;
    if (search) params.search = search;
    return await this.makeRequest('/leagues', params);
  }

  // Get international leagues from config
  getInternationalLeagues() {
    return this.internationalLeagues.map(league => ({
      ...league,
      // Add a normalized name for easier matching with API data
      normalizedName: league.league.toLowerCase()
    }));
  }

  // Get all leagues (combines API and international leagues)
  async getAllLeagues(id = null, name = null, countryId = null, country = null, type = null, season = null, search = null) {
    // Get leagues from API
    const apiLeagues = await this.getLeagues(id, name, countryId, country, type, season, search);

    // Get international leagues from config
    const internationalLeagues = this.getInternationalLeagues();

    // Combine both sources
    // IMPORTANT: The API response is FLAT, not nested!
    // Each item in response array looks like: { id, name, type, logo, country: {...}, seasons: [...] }
    const apiLeaguesArray = apiLeagues.response || [];

    // Debug: Check the actual structure of API response
    if (this.debug && apiLeaguesArray.length > 0) {
      this.log('Sample API league object:', JSON.stringify(apiLeaguesArray[0], null, 2));
    }

    // Create a Set of existing API league IDs to avoid duplicates
    // The API response has league data directly on the object (not nested under 'league')
    const apiLeagueIds = new Set(
      apiLeaguesArray
        .map(l => l.id) // Direct access to id, not l.league.id
        .filter(id => id != null)
        .map(id => Number(id))
    );

    // Debug logging (can be removed in production)
    if (this.debug) {
      this.log('API League IDs:', Array.from(apiLeagueIds));
      this.log('International League IDs:', internationalLeagues.map(l => l.leagueID));
    }

    // Transform international leagues to match the exact API format (flat structure)
    // IMPORTANT: Use real leagueID from JSON config (not fake IDs)
    const formattedInternationalLeagues = internationalLeagues
      .filter(league => {
        // Skip leagues that already exist in API response (avoid duplicates)
        // Convert both to numbers for proper comparison
        const leagueId = Number(league.leagueID);
        const isDuplicate = apiLeagueIds.has(leagueId);

        if (this.debug && isDuplicate) {
          this.log(`Skipping duplicate league: ${league.league} (ID: ${leagueId})`);
        }

        return league.leagueID && !isDuplicate;
      })
      .map(league => ({
        // Match the flat API structure (not nested under 'league')
        id: Number(league.leagueID), // Use REAL API league ID from JSON config
        name: league.league,
        type: 'League',
        logo: null,
        country: {
          name: league.country,
          code: null,
          flag: null
        },
        seasons: [], // International leagues may not have season data from config
        // Add metadata for reference
        _metadata: {
          notes: league.notes,
          isInternational: true
        }
      }));

    const mergedResponse = [...apiLeaguesArray, ...formattedInternationalLeagues];

    // Debug: Show final merged leagues
    if (this.debug) {
      this.log(`Total leagues after merge: ${mergedResponse.length}`);
      // Fixed: use flat structure (l.id, not l.league.id)
      this.log('Merged league IDs:', mergedResponse.map(l => `${l.id}:${l.name}`));
    }

    return {
      ...apiLeagues,
      response: mergedResponse
    };
  }

  // Get teams
  async getTeams(id = null, name = null, countryId = null, country = null, league = null, season = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (name) params.name = name;
    if (countryId) params.country_id = countryId;
    if (country) params.country = country;
    if (league) params.league = league;
    if (season) params.season = season;
    if (search) params.search = search;

    // This endpoint requires at least one parameter
    if (!id && !name && !countryId && !country && !league && !season && !search) {
      throw new Error('getTeams requires at least one parameter');
    }

    return await this.makeRequest('/teams', params);
  }

  // Get team statistics
  async getTeamStatistics(league, season, team, date = null) {
    if (!league || !season || !team) {
      throw new Error('getTeamStatistics requires league, season, and team parameters');
    }
    const params = { league, season, team };
    if (date) params.date = date;
    const data = await this.makeRequest('/teams/statistics', params);
    return data.response || [];
  }

  // GAMES & MATCHES

  // Get games
  async getGames(id = null, date = null, league = null, season = null, team = null, timezone = null) {
    const params = {};
    if (id) params.id = id;
    if (date) params.date = date;
    if (league) params.league = league;
    if (season) params.season = season;
    if (team) params.team = team;
    if (timezone) params.timezone = timezone;

    // This endpoint requires at least one parameter
    if (!id && !date && !league && !season && !team) {
      throw new Error('getGames requires at least one parameter');
    }

    // API requires: if season is provided, league must also be provided
    if (season && !league) {
      throw new Error('Season parameter requires league parameter');
    }

    // Use /games endpoint as per API documentation
    return await this.makeRequest('/games', params);
  }

  // Get today's games
  async getTodayGames(league = null, timezone = 'America/New_York') {
    const today = this.getTodayDate(); // Now uses ET-aware date
    return await this.getGames(null, today, league, null, null, timezone);
  }

  // Get head to head between teams
  async getHeadToHead(h2h, date = null, league = null, season = null, timezone = null) {
    if (!h2h) {
      throw new Error('getHeadToHead requires h2h parameter (team1-team2)');
    }
    const params = { h2h };
    if (date) params.date = date;
    if (league) params.league = league;
    if (season) params.season = season;
    if (timezone) params.timezone = timezone;

    return await this.makeRequest('/games/h2h', params);
  }

  // STANDINGS

  // Get standings
  async getStandings(league, season, team = null, stage = null, group = null) {
    if (!league || !season) {
      throw new Error('getStandings requires league and season parameters');
    }
    const params = { league, season };
    if (team) params.team = team;
    if (stage) params.stage = stage;
    if (group) params.group = group;
    return await this.makeRequest('/standings', params);
  }

  // Get standings stages
  async getStandingsStages(league, season) {
    if (!league || !season) {
      throw new Error('getStandingsStages requires league and season parameters');
    }
    const data = await this.makeRequest('/standings/stages', { league, season });
    return data.response || [];
  }

  // Get standings groups
  async getStandingsGroups(league, season) {
    if (!league || !season) {
      throw new Error('getStandingsGroups requires league and season parameters');
    }
    const data = await this.makeRequest('/standings/groups', { league, season });
    return data.response || [];
  }

  // ODDS

  // Get odds
  async getOdds(league = null, season = null, game = null, bookmaker = null, bet = null) {
    const params = {};
    if (league) params.league = league;
    if (season) params.season = season;
    if (game) params.game = game;
    if (bookmaker) params.bookmaker = bookmaker;
    if (bet) params.bet = bet;
    return await this.makeRequest('/odds', params);
  }

  // Get available bets
  async getBets(id = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (search) params.search = search;
    const data = await this.makeRequest('/odds/bets', params);
    return data.response || [];
  }

  // Get bookmakers
  async getBookmakers(id = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (search) params.search = search;
    const data = await this.makeRequest('/odds/bookmakers', params);
    return data.response || [];
  }

  // UTILITY FUNCTIONS

  // Get today's date in YYYY-MM-DD format (ET timezone)
  getTodayDate() {
    // TIMEZONE FIX: Always use ET (America/New_York) for date calculations
    // This ensures users anywhere in the world see the same "today" as NYC
    const etDate = new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });
    const date = new Date(etDate);
    // Format as YYYY-MM-DD for API compatibility
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Get current season (MLB season typically runs current year) in ET timezone
  getCurrentSeason() {
    // TIMEZONE FIX: Use ET to determine current year
    // Important for midnight crossover - ensures correct season year
    const etNow = new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });
    const now = new Date(etNow);
    return now.getFullYear();
  }

  // Get available seasons for current era (ET timezone)
  getAvailableSeasons() {
    const currentYear = this.getCurrentSeason(); // Now uses ET-aware year
    return [currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4];
  }

  // Get date range (ET timezone)
  getDateRange(days = 7, startFromToday = true) {
    const dates = [];
    const startOffset = startFromToday ? 0 : -days;

    for (let i = startOffset; i < (startFromToday ? days : 0); i++) {
      // TIMEZONE FIX: Calculate dates based on ET timezone
      // Prevents date drift for users in different timezones
      const etNow = new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });
      const date = new Date(etNow);
      date.setDate(date.getDate() + i);

      // Format as YYYY-MM-DD for API compatibility
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
    }
    return dates;
  }

  // Get upcoming dates with labels (ET timezone)
  getUpcomingDates(days = 7) {
    const dates = [];
    for (let i = 0; i < days; i++) {
      // TIMEZONE FIX: All date calculations use ET timezone
      // This ensures "Today" and "Tomorrow" are consistent with NYC time
      const etNow = new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });
      const date = new Date(etNow);
      date.setDate(date.getDate() + i);

      // Format as YYYY-MM-DD for API compatibility
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      let label;
      if (i === 0) label = 'Today';
      else if (i === 1) label = 'Tomorrow';
      else label = date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: 'America/New_York' // Ensure label also uses ET
      });

      dates.push({ value: dateStr, label });
    }
    return dates;
  }

  // Format time with timezone
  formatTime(dateString, timezone = 'America/New_York') {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  // Format date
  formatDate(dateString, timezone = 'America/New_York') {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  }

  // Get full date time
  getFullDateTime(dateString, timezone = 'America/New_York') {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  // Get status color for games (Baseball specific)
  getStatusColor(status) {
    const statusMap = {
      'NS': 'bg-gray-100 text-gray-800',       // Not Started
      'IN1': 'bg-green-100 text-green-800',    // Inning 1
      'IN2': 'bg-green-100 text-green-800',    // Inning 2
      'IN3': 'bg-green-100 text-green-800',    // Inning 3
      'IN4': 'bg-green-100 text-green-800',    // Inning 4
      'IN5': 'bg-green-100 text-green-800',    // Inning 5
      'IN6': 'bg-green-100 text-green-800',    // Inning 6
      'IN7': 'bg-green-100 text-green-800',    // Inning 7
      'IN8': 'bg-green-100 text-green-800',    // Inning 8
      'IN9': 'bg-green-100 text-green-800',    // Inning 9
      'POST': 'bg-yellow-100 text-yellow-800', // Postponed
      'CANC': 'bg-red-100 text-red-800',       // Cancelled
      'INTR': 'bg-orange-100 text-orange-800', // Interrupted
      'ABD': 'bg-red-100 text-red-800',        // Abandoned
      'FT': 'bg-slate-100 text-slate-800'      // Finished
    };
    return statusMap[status] || 'bg-gray-100 text-gray-800';
  }

  // Get league/division display name (Baseball specific)
  getLeagueName(league) {
    const leagueMap = {
      'American League': 'AL',
      'National League': 'NL',
      'AL East': 'AL East',
      'AL Central': 'AL Central', 
      'AL West': 'AL West',
      'NL East': 'NL East',
      'NL Central': 'NL Central',
      'NL West': 'NL West'
    };
    return leagueMap[league] || league;
  }

  // Get MLB teams list
  getMLBTeams() {
    // Hardcoded MLB teams data for quick access
    return [
      // American League East
      { id: 1, name: 'Baltimore Orioles', abbreviation: 'BAL', logo: 'https://media.api-sports.io/baseball/teams/1.png' },
      { id: 2, name: 'Boston Red Sox', abbreviation: 'BOS', logo: 'https://media.api-sports.io/baseball/teams/2.png' },
      { id: 3, name: 'New York Yankees', abbreviation: 'NYY', logo: 'https://media.api-sports.io/baseball/teams/3.png' },
      { id: 4, name: 'Tampa Bay Rays', abbreviation: 'TB', logo: 'https://media.api-sports.io/baseball/teams/4.png' },
      { id: 5, name: 'Toronto Blue Jays', abbreviation: 'TOR', logo: 'https://media.api-sports.io/baseball/teams/5.png' },
      
      // American League Central
      { id: 6, name: 'Chicago White Sox', abbreviation: 'CWS', logo: 'https://media.api-sports.io/baseball/teams/6.png' },
      { id: 7, name: 'Cleveland Guardians', abbreviation: 'CLE', logo: 'https://media.api-sports.io/baseball/teams/7.png' },
      { id: 8, name: 'Detroit Tigers', abbreviation: 'DET', logo: 'https://media.api-sports.io/baseball/teams/8.png' },
      { id: 9, name: 'Kansas City Royals', abbreviation: 'KC', logo: 'https://media.api-sports.io/baseball/teams/9.png' },
      { id: 10, name: 'Minnesota Twins', abbreviation: 'MIN', logo: 'https://media.api-sports.io/baseball/teams/10.png' },
      
      // American League West
      { id: 11, name: 'Houston Astros', abbreviation: 'HOU', logo: 'https://media.api-sports.io/baseball/teams/11.png' },
      { id: 12, name: 'Los Angeles Angels', abbreviation: 'LAA', logo: 'https://media.api-sports.io/baseball/teams/12.png' },
      { id: 13, name: 'Oakland Athletics', abbreviation: 'OAK', logo: 'https://media.api-sports.io/baseball/teams/13.png' },
      { id: 14, name: 'Seattle Mariners', abbreviation: 'SEA', logo: 'https://media.api-sports.io/baseball/teams/14.png' },
      { id: 15, name: 'Texas Rangers', abbreviation: 'TEX', logo: 'https://media.api-sports.io/baseball/teams/15.png' },
      
      // National League East
      { id: 16, name: 'Atlanta Braves', abbreviation: 'ATL', logo: 'https://media.api-sports.io/baseball/teams/16.png' },
      { id: 17, name: 'Miami Marlins', abbreviation: 'MIA', logo: 'https://media.api-sports.io/baseball/teams/17.png' },
      { id: 18, name: 'New York Mets', abbreviation: 'NYM', logo: 'https://media.api-sports.io/baseball/teams/18.png' },
      { id: 19, name: 'Philadelphia Phillies', abbreviation: 'PHI', logo: 'https://media.api-sports.io/baseball/teams/19.png' },
      { id: 20, name: 'Washington Nationals', abbreviation: 'WSH', logo: 'https://media.api-sports.io/baseball/teams/20.png' },
      
      // National League Central
      { id: 21, name: 'Chicago Cubs', abbreviation: 'CHC', logo: 'https://media.api-sports.io/baseball/teams/21.png' },
      { id: 22, name: 'Cincinnati Reds', abbreviation: 'CIN', logo: 'https://media.api-sports.io/baseball/teams/22.png' },
      { id: 23, name: 'Milwaukee Brewers', abbreviation: 'MIL', logo: 'https://media.api-sports.io/baseball/teams/23.png' },
      { id: 24, name: 'Pittsburgh Pirates', abbreviation: 'PIT', logo: 'https://media.api-sports.io/baseball/teams/24.png' },
      { id: 25, name: 'St. Louis Cardinals', abbreviation: 'STL', logo: 'https://media.api-sports.io/baseball/teams/25.png' },
      
      // National League West
      { id: 26, name: 'Arizona Diamondbacks', abbreviation: 'ARI', logo: 'https://media.api-sports.io/baseball/teams/26.png' },
      { id: 27, name: 'Colorado Rockies', abbreviation: 'COL', logo: 'https://media.api-sports.io/baseball/teams/27.png' },
      { id: 28, name: 'Los Angeles Dodgers', abbreviation: 'LAD', logo: 'https://media.api-sports.io/baseball/teams/28.png' },
      { id: 29, name: 'San Diego Padres', abbreviation: 'SD', logo: 'https://media.api-sports.io/baseball/teams/29.png' },
      { id: 30, name: 'San Francisco Giants', abbreviation: 'SF', logo: 'https://media.api-sports.io/baseball/teams/30.png' }
    ];
  }

  // Format innings score display
  formatInningsScore(innings) {
    if (!innings) return '';
    
    const inningsList = [];
    for (let i = 1; i <= 9; i++) {
      if (innings[i] !== null && innings[i] !== undefined) {
        inningsList.push(innings[i]);
      } else {
        break;
      }
    }
    
    if (innings.extra !== null && innings.extra !== undefined) {
      inningsList.push(`+${innings.extra}`);
    }
    
    return inningsList.join(' | ');
  }

  // Get team statistics summary
  formatTeamStats(stats) {
    if (!stats || !stats.response) return null;
    
    const data = stats.response;
    return {
      gamesPlayed: data.games?.played?.all || 0,
      wins: data.games?.wins?.all?.total || 0,
      losses: data.games?.loses?.all?.total || 0,
      winPercentage: data.games?.wins?.all?.percentage || '0.000',
      runsFor: data.points?.for?.total?.all || 0,
      runsAgainst: data.points?.against?.total?.all || 0,
      runsForAverage: data.points?.for?.average?.all || '0.0',
      runsAgainstAverage: data.points?.against?.average?.all || '0.0'
    };
  }


}

// Add a global method for easy testing in console
window.BaseballService = new BaseballService();

export default window.BaseballService;